import { TwinParameters } from '@teinor/erp/types/company/config/parameter'
import { Fragment } from 'react'
import { ButtonRedSecondary, ButtonSecondary } from '../../../../../../../../baseComponents/Button'
import { UserPermissionsSingle } from '../../../../../../../../baseComponents/CreateRoutering/types'
import { LittleModalCreateEditOutside } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import ModalDeleteFilled from '../../../../../../../../baseComponents/ModalsLayouts/ModalDeleteFilled'
import { ModalEditComponentProps } from '../../../../../../../../baseComponents/ModalsLayouts/types'
import TwinTrans from '../../../../../../../../baseComponents/TwinTrans'
import { FormElement, FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { valueOrDefaultValue } from '../../../../../../../../utils/globals/valueOrDefaultValue'
import useEditDeleteModal, { SetEditDeleteModalTy } from '../../../../../../../../utils/hooks/useEditDeleteModal'
import { useFetchText } from '../../../../../../../../utils/hooks/useFetchText'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'

interface EmailCompanyConfigProps {
    parameters: TwinDictionary | null
    userPermissions: UserPermissionsSingle
    getData: () => Promise<void>
}

export const EmailCompanyConfig: React.FC<EmailCompanyConfigProps> = ({ parameters, userPermissions, getData }: EmailCompanyConfigProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    let myRender = <ConfigureCompanyEmail setOpenModal={setOpenModal} />
    if (parameters?.emailCompanyEmail) {
        myRender = <ConfiguratedCompanyEmail setOpenModal={setOpenModal} email={parameters?.emailCompanyEmail} />
    }
    return (
        <Fragment>
            {myRender}
            {openModal?.type === 'create' ? <ModalCompanyEmailParametersConfig onSubmit={getData} setOpened={() => setOpenModal(null)} /> : null}
            {openModal?.type === 'edit' ? <ModalCompanyEmailParametersConfig onSubmit={getData} setOpened={() => setOpenModal(null)} allRowData={{ emailCompanyEmail: parameters?.emailCompanyEmail, emailCompanyHost: parameters?.emailCompanyHost, emailCompanyPort: parameters?.emailCompanyPort, emailNameFrom: parameters?.emailNameFrom}} userPermissions={userPermissions} /> : null}
            {openModal?.type === 'delete' ? <ModalDeleteCompanyEmailConfig onSubmit={getData} setOpened={() => setOpenModal(null)} /> : null}
        </Fragment>
    )
}

interface ConfiguratedCompanyEmailProps {
    email: string
    setOpenModal: SetEditDeleteModalTy
}

const ConfiguratedCompanyEmail: React.FC<ConfiguratedCompanyEmailProps> = ({ email, setOpenModal }) => {
    return (
        <div className='gap-x-20 gap-y-10 flex flex-row items-center flex-wrap'>
            <div className='flex gap-x-5'>
                <TwinTrans transKey={'CompanyEmailConfigured'}>Email configurado:</TwinTrans>
                <span>{email}</span>
            </div>
            <ButtonRedSecondary className='w-fit' type='button' onClick={() => setOpenModal({ type: 'delete', allRowData: {} })} key={'deleteCompanyEmailConfig'}><TwinTrans transKey={'deleteCompanyEmailConfig'}>Borrar configuración de email</TwinTrans></ButtonRedSecondary>
            <ButtonSecondary className='w-fit' type='button' onClick={() => setOpenModal({
                type: 'edit', allRowData: {}
            })} key={'adjustEmployeeEmailConfig'}><TwinTrans transKey={'adjustEmailConfig'}>Modificar configuración de email</TwinTrans></ButtonSecondary>
        </div>
    )
}

interface ConfigureCompanyEmailProps {
    setOpenModal: SetEditDeleteModalTy
}
const ConfigureCompanyEmail: React.FC<ConfigureCompanyEmailProps> = ({ setOpenModal }) => {
    return (
        <div className='gap-10 flex flex-row items-center'>
            <span><TwinTrans transKey={'CompanyEmailParametersConfig'}>Configurar parámetros de email</TwinTrans>:</span>
            <ButtonSecondary className='w-fit' type='button' onClick={() => setOpenModal({ type: 'create', allRowData: {} })} key={'configCompanyEmail'}><TwinTrans transKey={'Config'}>Configurar</TwinTrans></ButtonSecondary>
        </div>
    )
}

interface ModalDeleteCompanyEmailConfigProps extends ModalEditComponentProps { }

const ModalDeleteCompanyEmailConfig: React.FC<ModalDeleteCompanyEmailConfigProps> = ({ setOpened, onSubmit }) => {
    const { t } = useTwinTranslation()
    const parametersToDelete = ['emailCompanyPassword', 'emailCompanySecure', 'emailCompanyEmail', 'emailCompanyHost', 'emailCompanyPort', 'emailNameFrom']
    const { handleFetchText } = useFetchText({
        onSubmit,
        url: '/api/app/config/parameter/deleteParameters',
        data: {
            parameters: parametersToDelete
        }
    })

    return (
        <ModalDeleteFilled opened={true} setOpened={setOpened} onCancel={() => setOpened(null)} onAccept={handleFetchText} translations={{
            title: t('sureDeleteCompanyEmailConfig', '¿Seguro que quieres borrar la configuración de email?'),
            subtitle: t('YouWillBeAbleToConfigItAgain', 'Podrás volver a configurarla de nuevo'),
            buttonAccept: t('delete', 'Borrar'),
        }} />
    )
}

interface ModalCompanyEmailParametersConfigProps extends ModalEditComponentProps { }

const ModalCompanyEmailParametersConfig: React.FC<ModalCompanyEmailParametersConfigProps> = ({ setOpened, onSubmit, userPermissions, allRowData }: ModalCompanyEmailParametersConfigProps) => {
    const { t } = useTwinTranslation()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const passwordElement: FormElement<keyof TwinParameters>[] = []
    if (!allRowData) {
        passwordElement.push({
            name: 'emailCompanyPassword',
            label: t('emailPassword', 'Contraseña del email'),
            component: 'InputPasswordWithLabel',
            type: 'password',
            required: true
        })
    }
    const fields: FormRender<TwinParameters> = [
        {
            cols: 1,
            elements: [
                {
                    name: 'emailCompanyEmail',
                    label: t('email', 'Email'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                ...passwordElement,
                {
                    name: 'emailCompanyHost',
                    label: t('emailHost', 'Host del email'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true
                },
                {
                    name: 'emailCompanyPort',
                    label: t('emailPort', 'Puerto del email'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    defaultValue: '587',
                    required: true
                },
                {
                    name: 'emailNameFrom',
                    label: t('emailNameFrom', 'Nombe del remitente'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
                {
                    name: 'emailCompanySecure',
                    label: t('emailSecure', 'Seguridad del email'),
                    component: 'CustomSelect',
                    items: onOff,
                    value: 'off',
                    required: true,
                },
            ]
        },
    ]
    if (allRowData && userPermissions) {
        const parsedFields = valueOrDefaultValue(fields, userPermissions, allRowData)
        return (
            <LittleModalCreateEditOutside className={'flex flex-col'} fields={parsedFields} setOpened={setOpened} url={'/api/app/config/parameter/updateParameters'} translations={{
                title: t('parameters', 'Parámetros'),
                button: t('save', 'Guardar')
            }} haveButtonPermissions={true} onSubmit={onSubmit} />
        )
    }
    return (
        <LittleModalCreateEditOutside className={'flex flex-col'} fields={fields} setOpened={setOpened} url={'/api/app/config/parameter/updateParameters'} translations={{
            title: t('parameters', 'Parámetros'),
            button: t('save', 'Guardar')
        }} haveButtonPermissions={true} onSubmit={onSubmit} />
    )
}